import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Newspaper, ExternalLink, Shield, Clock } from "lucide-react";
import { fetchByTickerThunk } from "../../../redux/news/newsActionThunks";
import { selectNewsByTicker } from "../../../redux/news/newsSlice";

// Published News (4th Pillar) integration for the stock detail page --
// deliberately a separate, clearly-labeled section rather than merged into
// the existing SentimentNews widget above it: that component reads live,
// unpersisted Polygon sentiment/news lookups (search_news_sentiment.py),
// while this reads Stocker's own persisted, ticker-tagged, credibility-aware
// news_articles table (curated external + Elite-original). Same page,
// genuinely different data sources -- kept visually distinct rather than
// silently combined.
const RelatedPublishedNews = ({ symbol }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const articles = useSelector((s) => selectNewsByTicker(s, symbol));
  const loading = useSelector((s) => s.news?.byTickerLoading);

  useEffect(() => {
    if (symbol) dispatch(fetchByTickerThunk({ symbol, limit: 5 }));
  }, [dispatch, symbol]);

  if (!loading && articles.length === 0) return null;

  return (
    <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-500/10 border border-blue-500/25 flex items-center justify-center flex-shrink-0">
            <Newspaper size={16} className="text-blue-600 dark:text-blue-400" />
          </span>
          {t('stocks.relatedNews.heading', { symbol })}
        </h3>
        <Link to={`/news?ticker=${symbol}`} className="text-xs font-medium text-violet-700 dark:text-violet-400 hover:text-violet-800 dark:hover:text-violet-300">
          {t('stocks.relatedNews.viewAll')}
        </Link>
      </div>

      {loading && articles.length === 0 ? (
        <div className="space-y-2">
          {[1, 2].map((i) => <div key={i} className="h-14 bg-gray-100 dark:bg-gray-700/40 rounded-xl animate-pulse" />)}
        </div>
      ) : (
        <div className="space-y-2">
          {articles.map((a) => {
            const isElite = a.source_type === "elite_original";
            const to = isElite ? `/news/${a.id}` : a.article_url;
            const Card = (
              <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 hover:bg-gray-100 dark:hover:bg-gray-900/70 transition">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm text-gray-800 dark:text-gray-200 font-medium line-clamp-2">{a.title}</p>
                  {!isElite && <ExternalLink size={13} className="text-gray-400 flex-shrink-0 mt-0.5" />}
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-500">
                  {isElite ? (
                    <span className="flex items-center gap-1 text-violet-500 dark:text-violet-300">
                      <Shield size={11} /> {a.author_username}
                    </span>
                  ) : (
                    <span>{a.publisher_name || a.author}</span>
                  )}
                  {a.published_at && (
                    <span className="flex items-center gap-1">
                      <Clock size={11} /> {new Date(a.published_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            );
            return isElite ? (
              <Link key={a.id} to={to}>{Card}</Link>
            ) : (
              <a key={a.id} href={to} target="_blank" rel="noopener noreferrer">{Card}</a>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RelatedPublishedNews;
